import React, { useState, useMemo } from 'react';
import { IconButton, Menu, MenuItem, Divider, SxProps, Theme } from '@mui/material';
// import icon replaced by LanguageAvatar
// import LanguageIcon from '@mui/icons-material/Language';
import LanguageAvatar from 'components/LanguageAvatar';
import { useTranslation } from 'react-i18next';
import { useActor } from 'contexts/ActorContext';
import { writeActorAugment } from 'api';

/**
 * LanguageMenu renders a language selection icon button and menu.
 * In anonymous mode, only changes i18n language.
 * In signed-in mode, also updates actor language on server.
 */
export interface LanguageMenuProps {
  /** Optional sx override for the trigger element */
  sx?: SxProps<Theme>;
  /** Optional custom render function for the trigger element */
  renderTrigger?: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    ref: React.Ref<HTMLElement>;
    sx?: SxProps<Theme>;
  }) => React.ReactElement;
}
export default function LanguageMenu({ sx, renderTrigger }: LanguageMenuProps): React.ReactElement {
  const { actor, setActor } = useActor();
  const { i18n } = useTranslation();
  // Anchor for menu
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  // Ref for the trigger element (button)
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const handleClose = () => setAnchorEl(null);
  // Determine active language code (2-letter)
  const activeLang = actor?.actorLingo || i18n.language.split('-')[0] || 'en';
  // Popular languages list
  const popularLangs = useMemo(
    () => [
      'nl','es','ko','it','de','th','ru','pt','pl','id','zh','sv',
      'cs','en','ja','fr','ro','tr','ca','hu','uk','el','bg','ar',
      'sr','mk','lv','sl','hi','gl','da','ur','sk','he','fi','az',
      'lt','et','no','cy','fa','vi','ta','ms','hr','tl','bs','is'
    ],
    []
  );
  // Browser preferred languages
  const browserCodes = useMemo(() => {
    const raw = (navigator.languages || []).map(l => l.slice(0,2).toLowerCase());
    return Array.from(new Set(raw)).filter(code => code && code !== activeLang);
  }, [activeLang]);
  // Other popular languages not in browser list and not active
  const otherPopular = useMemo(
    () => popularLangs.filter(code => code !== activeLang && !browserCodes.includes(code)),
    [popularLangs, activeLang, browserCodes]
  );
  // Intl DisplayNames for formatting
  const activeDisplay = useMemo(
    () => new Intl.DisplayNames([activeLang], { type: 'language' }),
    [activeLang]
  );
  // Get display name in active language
  const formatLabel = (code: string) => {
    const activeName = activeDisplay.of(code) || code;
    return activeName.charAt(0).toLocaleUpperCase(activeLang) + activeName.slice(1);
  };
  // Sort browser and other lists
  const browserList = useMemo(
    () => [...browserCodes].sort((a,b) => formatLabel(a).localeCompare(formatLabel(b), activeLang)),
    [browserCodes, formatLabel, activeLang]
  );
  const otherList = useMemo(
    () => [...otherPopular].sort((a,b) => formatLabel(a).localeCompare(formatLabel(b), activeLang)),
    [otherPopular, formatLabel, activeLang]
  );
  // Handle language selection
  const handleSelect = async (code: string) => {
    handleClose();
    // Change i18n language
    try {
      if (actor) {
        // Signed-in: update server
        const resp = await writeActorAugment({ actorLingo: code });
        if (resp.actorMetadata) {
          setActor(resp.actorMetadata);
          await i18n.changeLanguage(resp.actorMetadata.actorLingo || code);
        } else {
          await i18n.changeLanguage(code);
        }
      } else {
        // Anonymous: only update UI
        await i18n.changeLanguage(code);
      }
    } catch (err) {
      console.error('Language change failed', err);
    }
  };
  const defaultTrigger = (
    <IconButton
      ref={triggerRef}
      size="large"
      onClick={handleOpen}
      aria-label={i18n.t('Language')}
      sx={sx}
    >
      {/* Replace language icon with flag avatar for active language */}
      <LanguageAvatar language={activeLang} />
    </IconButton>
  );
  const trigger = renderTrigger
    ? renderTrigger({ onClick: handleOpen, ref: triggerRef, sx })
    : defaultTrigger;
  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem selected>
          <LanguageAvatar language={activeLang} sx={{ mr: 1 }} />
          {formatLabel(activeLang)}
        </MenuItem>
        {browserList.map(code => (
          <MenuItem key={code} onClick={() => handleSelect(code)}>
            <LanguageAvatar language={code} sx={{ mr: 1 }} />
            {formatLabel(code)}
          </MenuItem>
        ))}
        <Divider />
        {otherList.map(code => (
          <MenuItem key={code} onClick={() => handleSelect(code)}>
            <LanguageAvatar language={code} sx={{ mr: 1 }} />
            {formatLabel(code)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}